import { apiClient } from '@/api/index';
import { components, operations } from '~/api-types';

export type OrganizationDto = components['schemas']['OrganizationResponseDto'];
export type AiSearchStrictness = OrganizationDto['aiSearchStrictness'];
export type PaginatedOrganizationDto =
  components['schemas']['PaginatedOrganizationResponseDto'];

export type GetOrganizationsListQueryParams =
  operations['AdminGetOrganizationsListEndpoint_handler']['parameters']['query'];
export type UpdateCurrentOrganizationBodyDto =
  components['schemas']['UpdateCurrentOrganizationBodyDto'];
export type AdminCreateOrganizationBodyDto =
  components['schemas']['AdminCreateOrganizationBodyDto'];
export type AdminUpdateOrganizationBodyDto =
  components['schemas']['AdminUpdateOrganizationBodyDto'];

export type AdminCloneOrganizationBodyDto = {
  name: string;
  slug: string;
  customDomain: string | null;
  emailFromAddress: string;
  forceEmailAddressVerification: boolean;
  locale?: AdminCreateOrganizationBodyDto['locale'];
};
export type CookieConsentBannerDto =
  components['schemas']['CookieConsentBannerResponseDto'];
export type MenuItemDto = components['schemas']['MenuItemResponseDto'];
export type UpdateOrCreateMenuItemDto =
  components['schemas']['UpdateOrCreateMenuItemDto'];

export type SupportFundsSettingsDto =
  components['schemas']['SupportFundsSettingsResponseDto'];

export type SupportFundsBusinessDto =
  components['schemas']['SupportFundsBusinessResponseDto'];

export const apiGetCurrentOrganization = async (): Promise<OrganizationDto> => {
  const { data } = await apiClient.get('/admin/organizations/current');

  return data;
};

export const apiGetOrganizationsList = async (
  params?: GetOrganizationsListQueryParams,
): Promise<PaginatedOrganizationDto> => {
  const { data } = await apiClient.get('/admin/organizations', {
    params,
  });

  return data;
};

export const apiUpdateCurrentOrganization = async (
  payload: Partial<UpdateCurrentOrganizationBodyDto>,
): Promise<OrganizationDto> => {
  const { data } = await apiClient.patch(
    '/admin/organizations/current',
    payload,
  );

  return data;
};

export const apiCreateOrganization = async (
  payload: AdminCreateOrganizationBodyDto,
): Promise<OrganizationDto> => {
  const { data } = await apiClient.post('/admin/organizations', payload);

  return data;
};

export const apiCloneOrganization = async (
  sourceOrganizationId: number,
  payload: AdminCloneOrganizationBodyDto,
): Promise<OrganizationDto> => {
  const { data } = await apiClient.post(
    `/admin/organizations/${sourceOrganizationId}/clone`,
    payload,
  );

  return data;
};

export const apiSuperAdminUpdateOrganization = async (
  payload: Partial<AdminUpdateOrganizationBodyDto>,
): Promise<OrganizationDto> => {
  const { data } = await apiClient.patch('/admin/organizations', payload);

  return data;
};

export const apiVerifyCurrentOrganizationEmailDomain =
  async (): Promise<OrganizationDto> => {
    const { data } = await apiClient.post(`/admin/organizations/verify-domain`);

    return data;
  };

export const apiVerifyNotificationEmailDomain =
  async (): Promise<OrganizationDto> => {
    const { data } = await apiClient.post(
      `/admin/organizations/verify-notification-email-domain`,
    );

    return data;
  };

export const apiVerifyLearningEmailDomain =
  async (): Promise<OrganizationDto> => {
    const { data } = await apiClient.post(
      `/admin/organizations/verify-learning-email-domain`,
    );

    return data;
  };

export const apiRetrieveActiveCookieConsentBanner = async (): Promise<{
  activeBanner: CookieConsentBannerDto | null;
  latestSubmission: {
    status: CookieConsentBannerValidationStatus;
    submittedAt: Date;
  } | null;
}> => {
  const { data } = await apiClient.get(
    '/admin/organizations/cookie-consent-banner/active',
  );

  return data;
};

export const apiSubmitNewCookieConsentBanner = async (
  payload: CookieConsentBannerDto,
): Promise<CookieConsentBannerDto> => {
  const { data } = await apiClient.post(
    '/admin/organizations/cookie-consent-banner/submit-new-script',
    payload,
  );

  return data;
};

export const apiGetSupportFunds =
  async (): Promise<SupportFundsSettingsDto> => {
    const { data } = await apiClient.get(
      '/admin/organizations/current/support-funds',
    );

    return data;
  };

export type UpdateSupportFundsBodyDto =
  components['schemas']['UpdateSupportFundsBodyDto'];

export const apiUpdateSupportFunds = async (
  payload: UpdateSupportFundsBodyDto,
): Promise<SupportFundsSettingsDto> => {
  const { data } = await apiClient.put(
    '/admin/organizations/current/support-funds',
    payload,
  );

  return data;
};

export const apiImportSupportFunds = async (csv: string) => {
  const { data } = await apiClient.post('/admin/support-funds/import', {
    csv,
  });

  return data as { processedBusinessIds: number[]; errors: string[] };
};

export const apiGetSupportFundsBusinesses = async (): Promise<
  SupportFundsBusinessDto[]
> => {
  const { data } = await apiClient.get('/admin/support-funds/businesses');
  return data;
};
